import { useEffect, useRef, useState } from "react";

interface Props {
  /** The text shown in the code field and written to the clipboard on click. */
  text: string;
}

/**
 * A read-only code field with a Copy button. Shows "Copied!"/"Failed" feedback
 * for 2s. Shared by InstallInClaude (marketplace URL) and HelpPage (CLI commands).
 */
export default function CopyButton({ text }: Props) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel a pending reset if the component unmounts mid-flash.
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const flash = (state: "copied" | "failed") => {
    setCopied(state === "copied");
    setFailed(state === "failed");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setCopied(false); setFailed(false); }, 2000);
  };

  const handleCopy = () => {
    // navigator.clipboard is undefined on non-secure (http) contexts; calling
    // .writeText on it throws synchronously, which a .catch wouldn't see.
    if (!navigator.clipboard) {
      flash("failed");
      return;
    }
    navigator.clipboard.writeText(text).then(() => flash("copied")).catch(() => flash("failed"));
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", backgroundColor: "var(--color-surface-secondary)", borderRadius: "6px", padding: "0.5rem 0.75rem", margin: "0.5rem 0" }}>
      <code title={text} style={{ flex: 1, fontSize: "0.8rem", color: "var(--color-text-primary)", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {text}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        title={failed ? "Copy failed — select the text manually" : "Copy"}
        style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", padding: "2px", fontSize: "0.75rem", color: copied ? "var(--color-success)" : failed ? "var(--color-error)" : "var(--color-text-muted)" }}
      >
        {copied ? "Copied!" : failed ? "Failed" : "Copy"}
      </button>
    </div>
  );
}
